import { Check, X, Share2, Clock, Users } from 'lucide-react';

export const DailyChallenge = ({
  dailyChallenge,
  recoveryMode,
  shakeDaily,
  showRerollAnimation,
  getTimeRemaining,
  dailyChallengeCount,
  completeChallenge,
  shareToSocial,
  swapUsed,
  onSwap,
  isPremium,
  hardModeActive,
  hardModeChallenge,
  onToggleHardMode,
}) => {
  // Derived display values
  const displayText   = hardModeActive && hardModeChallenge ? hardModeChallenge.text   : dailyChallenge.text;
  const displayPoints = hardModeActive && hardModeChallenge ? hardModeChallenge.points : dailyChallenge.points;

  // Card border: red when hard mode, lime normally
  const cardBorder = hardModeActive
    ? 'border-red-500 hover:border-red-400'
    : 'border-lime-500 hover:border-lime-400';
  const headerBorder = hardModeActive ? 'border-red-500' : 'border-lime-500';
  const cardGlow     = hardModeActive
    ? { idle: '0 4px 6px rgba(0,0,0,0.3)', hover: '0 20px 40px rgba(239, 68, 68, 0.6)' }
    : { idle: '0 4px 6px rgba(0,0,0,0.3)', hover: '0 20px 40px rgba(132, 204, 22, 0.6)' };

  return (
    <>
      {recoveryMode && !dailyChallenge.completed && (
        <div className={`bg-gradient-to-r from-orange-600 to-red-600 border-2 sm:border-3 border-orange-400 p-4 sm:p-5 ${shakeDaily ? 'animate-shake' : ''}`}>
          <h3 className="text-lg sm:text-xl md:text-2xl font-black text-white uppercase mb-2 flex items-center gap-2 flex-wrap">
            🔥 RECOVERY MODE ACTIVE 🔥
          </h3>
          <p className="text-orange-100 font-bold text-sm sm:text-base mb-3">
            Your streak broke. Don't bitch out now.
          </p>
          <div className="bg-black border-2 border-orange-300 p-2.5 sm:p-3 inline-block">
            <p className="text-orange-400 font-black text-sm sm:text-base md:text-lg">
              NEXT SEND = 2X POINTS ({displayPoints} → {displayPoints * 2})
            </p>
          </div>
        </div>
      )}

      <div
        className={`bg-gradient-to-br from-gray-900 to-black p-3 sm:p-4 md:p-6 border-2 sm:border-3 md:border-4 ${cardBorder} ${shakeDaily ? 'animate-shake' : ''} ${showRerollAnimation ? 'animate-reroll-spin' : ''} transition-all duration-300 hover:scale-105`}
        style={{ zIndex: 0, boxShadow: cardGlow.idle }}
        onMouseEnter={(e) => e.currentTarget.style.boxShadow = cardGlow.hover}
        onMouseLeave={(e) => e.currentTarget.style.boxShadow = cardGlow.idle}
      >
        {/* Hard Mode banner */}
        {hardModeActive && (
          <div className="bg-red-950 border border-red-600 px-3 py-1.5 mb-3 flex items-center gap-2">
            <span className="text-red-400 text-xs font-black uppercase tracking-widest animate-pulse">💀 HARD MODE</span>
            <span className="text-red-600 text-xs font-bold uppercase">2x–3x POINTS</span>
          </div>
        )}

        {/* Custom challenge banner */}
        {dailyChallenge.isCustom && (
          <div className="bg-purple-950 border border-purple-600 px-3 py-1.5 mb-3 flex items-center gap-2">
            <span className="text-purple-400 text-xs font-black uppercase tracking-widest">🎯 CUSTOM CHALLENGE</span>
            <span className="text-purple-600 text-xs font-bold uppercase">set by you</span>
          </div>
        )}

        {/* Community Pick banner */}
        {dailyChallenge.isCommunityPick && (
          <div className="bg-amber-900 border border-amber-600 px-3 py-1.5 mb-3 flex items-center gap-2">
            <span className="text-amber-400 text-xs font-black uppercase tracking-widest">⚗️ COMMUNITY PICK</span>
            <span className="text-amber-600 text-xs font-bold">by {dailyChallenge.submitterUsername}</span>
          </div>
        )}

        <div className={`flex flex-col sm:flex-row sm:items-start justify-between mb-3 sm:mb-4 pb-2 sm:pb-3 border-b-2 ${headerBorder} gap-2 sm:gap-3`}>
          <div className="flex-1">
            <h2 className={`text-xl sm:text-2xl md:text-3xl font-black uppercase tracking-tight ${hardModeActive ? 'text-red-500' : 'text-lime-500'}`}>
              TODAY'S CHALLENGE
            </h2>
          </div>
          <div className="flex flex-col sm:items-end gap-1">
            <div className="flex items-center gap-2">
              {!dailyChallenge.completed && getTimeRemaining('daily') && !getTimeRemaining('daily').expired && (
                <div className={`px-2 sm:px-3 py-1 border-2 ${getTimeRemaining('daily').isWarning
                  ? 'bg-red-600 border-red-400 animate-pulse'
                  : `bg-black ${hardModeActive ? 'border-red-500' : 'border-lime-400'}`
                  }`}>
                  <div className="flex items-center gap-1">
                    <Clock size={14} className={getTimeRemaining('daily').isWarning ? 'text-white' : hardModeActive ? 'text-red-400' : 'text-lime-400'} />
                    <span className={`font-black text-xs uppercase ${getTimeRemaining('daily').isWarning ? 'text-white' : hardModeActive ? 'text-red-400' : 'text-lime-400'}`}>
                      {getTimeRemaining('daily').hours}H {getTimeRemaining('daily').minutes}M
                    </span>
                  </div>
                </div>
              )}
              <span className={`text-black px-2 sm:px-3 md:px-4 py-1 font-black uppercase text-xs sm:text-sm tracking-wider ${hardModeActive ? 'bg-red-500' : 'bg-lime-500'}`}>
                {dailyChallenge.category}
              </span>
            </div>
            {hardModeActive ? (
              <span className="text-red-400 text-xs font-black">
                +{displayPoints} PTS (HARD)
              </span>
            ) : (
              <span className="text-lime-400 text-xs font-black">
                +{displayPoints} POINTS
              </span>
            )}
          </div>
        </div>

        <p className="text-white text-base sm:text-lg md:text-xl font-bold leading-relaxed mb-3 sm:mb-4 md:mb-6">{displayText}</p>

        {dailyChallengeCount > 0 && !hardModeActive && (
          <div className="mb-3 sm:mb-4 flex items-center gap-2 text-lime-400">
            <Users size={16} />
            <span className="font-bold text-sm">
              {dailyChallengeCount} {dailyChallengeCount === 1 ? 'person' : 'people'} sent this today
            </span>
          </div>
        )}

        {!dailyChallenge.completed ? (
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => completeChallenge(true)}
                className={`flex-1 py-4 sm:py-5 font-black text-lg sm:text-xl flex items-center justify-center gap-2 transition-all duration-200 uppercase tracking-wider border-2 active:scale-95 touch-manipulation min-h-[64px] hover:scale-105 ${
                  hardModeActive
                    ? 'bg-red-600 hover:bg-red-500 text-white border-red-400'
                    : 'bg-lime-500 hover:bg-lime-400 text-black border-lime-400'
                }`}
                style={{ boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = hardModeActive ? '0 10px 30px rgba(239,68,68,0.7)' : '0 10px 30px rgba(132,204,22,0.7)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.3)'}
              >
                <Check size={24} />
                {hardModeActive ? '💀 SENT IT (HARD)' : 'SENT IT'}
              </button>
              <button
                onClick={() => completeChallenge(false)}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-400 py-4 sm:py-5 font-black text-lg sm:text-xl flex items-center justify-center gap-2 transition-all duration-200 uppercase tracking-wider border-2 border-gray-700 active:scale-95 touch-manipulation min-h-[64px] hover:scale-105"
                style={{ boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 10px 30px rgba(75, 85, 99, 0.7)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.3)'}
              >
                <X size={24} />
                BITCHED OUT
              </button>
            </div>

            {/* Hard Mode toggle — premium only */}
            {isPremium && (
              <button
                onClick={onToggleHardMode}
                className={`w-full py-2.5 font-black text-sm uppercase tracking-wider border-2 transition-all duration-200 touch-manipulation flex items-center justify-center gap-2 ${
                  hardModeActive
                    ? 'bg-red-900 border-red-500 text-red-300 hover:bg-red-800 active:scale-95'
                    : 'bg-black border-red-600 text-red-500 hover:bg-red-900 active:scale-95'
                }`}
              >
                💀 {hardModeActive ? 'HARD MODE ON — CLICK TO DISABLE' : 'HARD MODE'}
              </button>
            )}

            <button
              onClick={onSwap}
              disabled={swapUsed || hardModeActive}
              className={`w-full py-2.5 font-black text-sm uppercase tracking-wider border-2 transition-all duration-200 touch-manipulation flex items-center justify-center gap-2 ${
                swapUsed || hardModeActive
                  ? 'border-gray-700 text-gray-600 cursor-not-allowed'
                  : 'border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black active:scale-95'
              }`}
            >
              🔀 {swapUsed ? 'SWAP IT USED THIS WEEK' : hardModeActive ? 'SWAP DISABLED IN HARD MODE' : 'SWAP IT'}
            </button>
          </div>
        ) : dailyChallenge.success ? (
          <div className="space-y-3">
            <div className="bg-lime-500 border-2 border-lime-400 py-4 sm:py-5 text-center font-black text-black text-base sm:text-lg uppercase tracking-wider">
              ✓ CHALLENGE COMPLETE • COME BACK TOMORROW
            </div>
            <button
              onClick={() => shareToSocial('challenge', { challenge: dailyChallenge.text })}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white py-3 sm:py-4 font-black text-base sm:text-lg flex items-center justify-center gap-2 transition-all uppercase tracking-wider border-3 border-blue-400"
            >
              <Share2 size={20} />
              TELL EVERYONE
            </button>
          </div>
        ) : (
          <div className="bg-gray-900 border-2 border-gray-700 py-4 sm:py-5 text-center font-black text-gray-400 text-base sm:text-lg uppercase tracking-wider">
            ✗ BITCHED OUT • RECOVERY MODE ACTIVE
          </div>
        )}
      </div>
    </>
  );
};
